/**
 * Collection stage recommendation types — persisted advisory records only.
 *
 * Recommendations do not mutate collection case stage, AR, flows, or legal state.
 */

export const COLLECTION_RECOMMENDED_STAGES = [
  'reminder',
  'visit',
  'revisit',
  'final_notice',
  'legal_prep',
] as const;

export type CollectionRecommendedStage = (typeof COLLECTION_RECOMMENDED_STAGES)[number];

export const COLLECTION_RECOMMENDATION_KINDS = ['stage_recommendation'] as const;

export type CollectionRecommendationKind = (typeof COLLECTION_RECOMMENDATION_KINDS)[number];

export const COLLECTION_RECOMMENDATION_STATUSES = ['active', 'dismissed', 'expired'] as const;

export type CollectionRecommendationStatus = (typeof COLLECTION_RECOMMENDATION_STATUSES)[number];

import type { CollectionRecommendationSourceSnapshotV1 } from '@shared/collection/collection-recommendation-source-snapshot';

export type CollectionRecommendationSourceSnapshot = CollectionRecommendationSourceSnapshotV1;

export type CollectionRecommendationRationalePayload = Record<string, unknown>;

export interface CollectionRecommendation {
  id: string;
  tenantId: string;
  collectionCaseId: string;
  recommendationKind: CollectionRecommendationKind;
  recommendedStage: CollectionRecommendedStage;
  ruleProfileId: string;
  ruleProfileVersion: string;
  evaluatorVersion: string;
  sourceSnapshot: CollectionRecommendationSourceSnapshot;
  sourceSnapshotHash: string;
  rationaleCode: string;
  rationalePayload: CollectionRecommendationRationalePayload;
  caveats: string[];
  status: CollectionRecommendationStatus;
  createdAt: string;
  expiresAt: string | null;
  expiredAt: string | null;
  dismissedAt: string | null;
  dismissedByActorId: string | null;
  dismissalReasonCode: string | null;
  supersedesRecommendationId: string | null;
}

export interface CreateCollectionRecommendationInput {
  tenantId: string;
  collectionCaseId: string;
  recommendationKind: CollectionRecommendationKind;
  recommendedStage: CollectionRecommendedStage;
  ruleProfileId: string;
  ruleProfileVersion: string;
  evaluatorVersion: string;
  sourceSnapshot: CollectionRecommendationSourceSnapshot;
  sourceSnapshotHash: string;
  rationaleCode: string;
  rationalePayload: CollectionRecommendationRationalePayload;
  caveats: string[];
  expiresAt?: string | null;
  supersedesRecommendationId?: string | null;
}

export interface CollectionRecommendationEvaluationIdentity {
  tenantId: string;
  collectionCaseId: string;
  recommendationKind: CollectionRecommendationKind;
  sourceSnapshotHash: string;
  ruleProfileVersion: string;
  evaluatorVersion: string;
}

export type CollectionRecommendationDismissLifecycleUpdate = {
  status: 'dismissed';
  dismissedAt: string;
  dismissedByActorId: string;
  dismissalReasonCode?: string | null;
};

export type CollectionRecommendationExpireLifecycleUpdate = {
  status: 'expired';
  expiredAt: string;
};

export type CollectionRecommendationLifecycleUpdate =
  | CollectionRecommendationDismissLifecycleUpdate
  | CollectionRecommendationExpireLifecycleUpdate;

export function isCollectionRecommendedStage(value: string): value is CollectionRecommendedStage {
  return (COLLECTION_RECOMMENDED_STAGES as readonly string[]).includes(value);
}

export function isCollectionRecommendationKind(
  value: string
): value is CollectionRecommendationKind {
  return (COLLECTION_RECOMMENDATION_KINDS as readonly string[]).includes(value);
}

export function isCollectionRecommendationStatus(
  value: string
): value is CollectionRecommendationStatus {
  return (COLLECTION_RECOMMENDATION_STATUSES as readonly string[]).includes(value);
}
